import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useAuth } from '../contexts/AuthContext';
import {
  ApiBusiness,
  appendIosPhoneFields,
  fetchCategories,
  fetchSocialTypes,
  fetchUserBusinesses,
  getBusinessLogoUrl,
  postUpdateBusinessIos,
  SocialTypeItem,
} from '../services/api';
import { useT } from '../i18n/useT';
import type { StringKey } from '../i18n/strings';

type VisiWizardPageProps = {
  initialName?: string;
  promoterCode?: string;
};

function decodeName(raw: string | undefined): string {
  if (!raw) return '';
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function VisiWizardPage({ initialName = '', promoterCode }: VisiWizardPageProps = {}) {
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [searchParams] = useSearchParams();
  const promoCode = promoterCode || searchParams.get('code');
  const { user } = useAuth();
  const t = useT();
  const isEdit = Boolean(businessId && /^\d+$/.test(businessId));
  const steps = useMemo(
    () =>
      [
        'visiWizardStepBasics',
        'visiWizardStepCategory',
        'visiWizardStepContacts',
        'visiWizardStepMedia',
        'visiWizardStepReview',
      ] as StringKey[],
    []
  );

  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState(initialName);
  const [username, setUsername] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [subsubcategoryId, setSubsubcategoryId] = useState('');
  const [email1, setEmail1] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phoneWhatsapp, setPhoneWhatsapp] = useState(false);
  const [phoneSignal, setPhoneSignal] = useState(false);
  const [phoneThreema, setPhoneThreema] = useState(false);
  const [socialTypes, setSocialTypes] = useState<SocialTypeItem[]>([]);
  const [socialRows, setSocialRows] = useState<Array<{ typeName: string; value: string }>>([
    { typeName: '', value: '' },
  ]);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [tree, setTree] = useState<
    Array<{
      id: number;
      name: string;
      subcategories?: Array<{
        id: number;
        name: string;
        subsubcategories?: Array<{ id: number; name: string }>;
      }>;
    }>
  >([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState<ApiBusiness | null>(null);

  useEffect(() => {
    if (!user?.token) return;
    setLoadingTree(true);
    fetchCategories(undefined, user.token)
      .then((res) => {
        setTree((res.categories || []) as typeof tree);
      })
      .catch(() => setLoadErr(t('visiWizardLoadCategoriesFailed')))
      .finally(() => setLoadingTree(false));
    fetchSocialTypes(user.token)
      .then(setSocialTypes)
      .catch(() => undefined);
  }, [user?.token]);

  useEffect(() => {
    if (!isEdit || !user?.token || !businessId) return;
    const id = parseInt(businessId, 10);
    fetchUserBusinesses(user.token)
      .then((res) => {
        const b = (res.businesses || []).find((x) => x.id === id);
        if (!b) {
          setLoadErr(t('visiWizardNotFound'));
          return;
        }
        setExisting(b);
        setName(decodeName(b.name));
        setUsername((b.username || '').replace(/^@/, ''));
        setEmail1(b.emails?.[0]?.email || '');
        setPhone1((b.phones?.[0]?.phone || '').replace(/^\+/, ''));
        const extras = b.phones?.[0]?.extras || [];
        setPhoneWhatsapp(extras.some((e) => (e.name || '').toLowerCase() === 'whatsapp' && (e.selected ?? 0) === 1));
        setPhoneSignal(extras.some((e) => (e.name || '').toLowerCase() === 'signal' && (e.selected ?? 0) === 1));
        setPhoneThreema(extras.some((e) => (e.name || '').toLowerCase() === 'threema' && (e.selected ?? 0) === 1));
        const socials = (b.socials || [])
          .map((s) => ({
            typeName: s.type?.name || '',
            value: (s.social || s.url || '').trim(),
          }))
          .filter((s) => s.typeName && s.value);
        setSocialRows(socials.length ? socials : [{ typeName: '', value: '' }]);
        if (b.subsubcategory?.id) {
          setSubsubcategoryId(String(b.subsubcategory.id));
        }
        if (b.subcategory?.id) {
          setSubcategoryId(String(b.subcategory.id));
        }
        if (b.category?.id) {
          setCategoryId(String(b.category.id));
        }
      })
      .catch(() => setLoadErr(t('visiWizardLoadFailed')));
  }, [isEdit, businessId, user?.token]);

  const subsForCategory = useMemo(() => {
    const c = tree.find((x) => String(x.id) === categoryId);
    return c?.subcategories || [];
  }, [tree, categoryId]);

  const subsubsForSub = useMemo(() => {
    const s = subsForCategory.find((x) => String(x.id) === subcategoryId);
    return s?.subsubcategories || [];
  }, [subsForCategory, subcategoryId]);

  const logoPreview = useMemo(() => {
    if (logoFile) return URL.createObjectURL(logoFile);
    if (existing?.logo) return getBusinessLogoUrl(existing.logo) || null;
    return null;
  }, [logoFile, existing?.logo]);

  useEffect(() => {
    return () => {
      if (logoFile && logoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoFile, logoPreview]);

  const canNextBasics = name.trim().length > 0 && /^[a-z0-9._]{4,20}$/.test(username.trim());
  const canNextCategory = Boolean(subcategoryId);
  const canNextContacts = EMAIL_RE.test(email1.trim());

  const buildFormData = (): FormData => {
    const fd = new FormData();
    if (isEdit && businessId) {
      fd.append('business_id', businessId);
    }
    fd.append('name', name.trim());
    fd.append('username', username.trim().replace(/^@/, ''));
    fd.append('chosenType', '1');
    fd.append('user_id', String(user?.id ?? ''));
    fd.append('place_type', '2');
    fd.append('lat', '0');
    fd.append('lng', '0');
    fd.append('address', '');
    fd.append('biography', ' ');
    fd.append('web', '');
    fd.append('email1', email1.trim());
    if (phone1.trim()) {
      appendIosPhoneFields(fd, phone1.trim(), 1, '41', {
        whatsapp: phoneWhatsapp,
        signal: phoneSignal,
        threema: phoneThreema,
      });
    }
    let socialIdx = 1;
    for (const row of socialRows) {
      if (!row.typeName || !row.value.trim()) continue;
      fd.append(`${row.typeName},${socialIdx}`, row.value.trim());
      socialIdx += 1;
    }
    if (subsubcategoryId) {
      fd.append('chosenSubsubcategory', subsubcategoryId);
    } else if (subcategoryId) {
      fd.append('chosenSubcategory', subcategoryId);
    }
    if (promoCode) {
      fd.append('code', promoCode);
    }
    if (logoFile) {
      fd.append('image1', logoFile);
    }
    if (videoFile) {
      fd.append('video', videoFile);
    }
    return fd;
  };

  const handleSubmit = async () => {
    if (!user?.token) return;
    setSubmitErr(null);
    setSubmitting(true);
    try {
      const fd = buildFormData();
      const res = await postUpdateBusinessIos(fd, user.token);
      const newId = res.business_id ?? (isEdit ? parseInt(businessId!, 10) : undefined);
      const uname = username.trim().replace(/^@/, '');
      if (uname) {
        navigate(`/${uname}`);
      } else if (newId) {
        navigate('/profile');
      } else {
        navigate('/profile');
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('visiWizardSaveFailed');
      setSubmitErr(
        /413|Content Too Large|Payload/i.test(msg)
          ? t('visiWizardFileTooLarge')
          : msg
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!user?.token) {
    return (
      <AppShell title={t('searchTabVisi')} showBackButton maxWidth="sm" showCompanion={false}>
        <Typography>{t('visiWizardSignIn')}</Typography>
      </AppShell>
    );
  }

  return (
    <AppShell title={isEdit ? t('visiWizardEditTitle') : t('visiWizardCreateTitle')} showBackButton maxWidth="sm" showCompanion={false}>
        {promoCode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('visiWizardPromoterHint', { code: promoCode })}
          </Alert>
        )}
        {loadErr && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadErr}
          </Alert>
        )}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((labelKey) => (
            <Step key={labelKey}>
              <StepLabel>{t(labelKey)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label={t('visiWizardDisplayName')} value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField
              label={t('visiWizardUsername')}
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase())}
              fullWidth
              required
              helperText={t('visiWizardUsernameHint')}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="contained" disabled={!canNextBasics} onClick={() => setActiveStep(1)}>
                {t('next')}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {loadingTree ? t('visiWizardLoadingCategories') : t('visiWizardPickCategory')}
            </Typography>
            <TextField
              select
              label={t('visiWizardCategory')}
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
                setSubsubcategoryId('');
              }}
              fullWidth
            >
              {tree.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            {categoryId && (
              <TextField
                select
                label={t('visiWizardSubcategory')}
                value={subcategoryId}
                onChange={(e) => {
                  setSubcategoryId(e.target.value);
                  setSubsubcategoryId('');
                }}
                fullWidth
              >
                {subsForCategory.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {subcategoryId && subsubsForSub.length > 0 && (
              <TextField
                select
                label={t('visiWizardSpecialization')}
                value={subsubcategoryId}
                onChange={(e) => setSubsubcategoryId(e.target.value)}
                fullWidth
              >
                <MenuItem value="">{t('visiWizardNone')}</MenuItem>
                {subsubsForSub.map((ss) => (
                  <MenuItem key={ss.id} value={String(ss.id)}>
                    {ss.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button onClick={() => setActiveStep(0)}>{t('back')}</Button>
              <Button variant="contained" disabled={!canNextCategory} onClick={() => setActiveStep(2)}>
                {t('next')}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('visiWizardEmail')}
              type="email"
              value={email1}
              onChange={(e) => setEmail1(e.target.value)}
              fullWidth
              required
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText={t('visiWizardEmailHint')}
              error={email1.trim().length > 0 && !EMAIL_RE.test(email1.trim())}
              sx={{
                '& .MuiInputLabel-root': { backgroundColor: 'transparent' },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-input': { pt: 1.75, pb: 1.75 },
              }}
            />
            <TextField
              label={t('visiWizardPhone')}
              value={phone1}
              onChange={(e) => setPhone1(e.target.value)}
              fullWidth
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              helperText={t('visiWizardPhoneHint')}
              sx={{
                '& .MuiInputLabel-root': { backgroundColor: 'transparent' },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)',
                },
                '& .MuiOutlinedInput-input': { pt: 1.75, pb: 1.75 },
              }}
            />
            {phone1.trim().length > 0 && (
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={phoneWhatsapp} onChange={(e) => setPhoneWhatsapp(e.target.checked)} />}
                  label={t('visiWizardPhoneWhatsapp')}
                />
                <FormControlLabel
                  control={<Checkbox checked={phoneSignal} onChange={(e) => setPhoneSignal(e.target.checked)} />}
                  label={t('visiWizardPhoneSignal')}
                />
                <FormControlLabel
                  control={<Checkbox checked={phoneThreema} onChange={(e) => setPhoneThreema(e.target.checked)} />}
                  label={t('visiWizardPhoneThreema')}
                />
              </FormGroup>
            )}
            <Typography variant="subtitle2">{t('visiWizardSocials')}</Typography>
            {socialRows.map((row, idx) => (
              <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('visiEditNetwork')}</InputLabel>
                  <Select
                    label={t('visiEditNetwork')}
                    value={row.typeName}
                    onChange={(e) => {
                      const next = [...socialRows];
                      next[idx] = { ...next[idx], typeName: e.target.value };
                      setSocialRows(next);
                    }}
                  >
                    {socialTypes.map((s) => (
                      <MenuItem key={s.name} value={s.name}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('visiEditHandleOrUrl')}
                  value={row.value}
                  onChange={(e) => {
                    const next = [...socialRows];
                    next[idx] = { ...next[idx], value: e.target.value };
                    setSocialRows(next);
                  }}
                  fullWidth
                />
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={() => setSocialRows([...socialRows, { typeName: '', value: '' }])}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {t('visiWizardAddSocial')}
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button onClick={() => setActiveStep(1)}>{t('back')}</Button>
              <Button variant="contained" disabled={!canNextContacts} onClick={() => setActiveStep(3)}>
                {t('next')}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="outlined" component="label">
              {t('visiWizardLogoImage')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setLogoFile(f || null);
                  e.target.value = '';
                }}
              />
            </Button>
            <Button variant="outlined" component="label">
              {t('visiWizardVideo')}
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setVideoFile(f || null);
                  e.target.value = '';
                }}
              />
            </Button>
            {logoPreview && (
              <Box
                component="img"
                src={logoPreview}
                alt={t('visiWizardLogoPreview')}
                sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 1 }}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button onClick={() => setActiveStep(2)}>{t('back')}</Button>
              <Button variant="contained" onClick={() => setActiveStep(4)}>
                {t('next')}
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 4 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              <strong>{t('visiWizardReviewName')}</strong> {name}
            </Typography>
            <Typography variant="body2">
              <strong>{t('visiWizardReviewUsername')}</strong> @{username}
            </Typography>
            <Typography variant="body2">
              <strong>{t('visiWizardReviewEmail')}</strong> {email1}
            </Typography>
            <Typography variant="body2">
              <strong>{t('visiWizardReviewPhone')}</strong> {phone1 || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>{t('visiWizardReviewCategory')}</strong> {categoryId} / {subcategoryId}
              {subsubcategoryId ? ` / ${subsubcategoryId}` : ''}
            </Typography>
            {submitErr && <Alert severity="error">{submitErr}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Button onClick={() => setActiveStep(3)} disabled={submitting}>
                {t('back')}
              </Button>
              <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting || !canNextContacts}>
                {submitting ? t('saving') : t('save')}
              </Button>
            </Box>
          </Box>
        )}
    </AppShell>
  );
}
